import type { PaginationState, SortingState } from '@tanstack/react-table';
import { DateTime } from '@ksyos/date-time';
import type { OutpatientClinicTask } from '@common/contexts/support/domain/models/outpatientClinicTaskModel';
import { outpatientClinicTaskMapper } from '@common/contexts/support/infrastructure/mappers/outpatientClinicTaskMapper';
import { SortDirection } from '@common/enums';
import { OutpatientClinic } from '@frontend/components/pages/outpatientClinic/constants/apiEndpoints';
import { postJson } from '@frontend/store/fetch';
import type { OutpatientClinicTaskListFilter } from '../types/outpatientClinicTaskListFilter';

export type FetchOutpatientClinicTasksProps = {
    abortSignal: AbortSignal;
    filter: OutpatientClinicTaskListFilter;
    pagination: PaginationState;
    sorting?: SortingState;
};

export async function fetchOutpatientClinicTasks({
    abortSignal,
    filter,
    pagination,
    sorting,
}: FetchOutpatientClinicTasksProps): Promise<{ items: OutpatientClinicTask[]; total: number }> {
    const typeFilter = [
        ...(filter.category?.map((category) => ({ category: { id: category.id } })) || []),
        ...(filter.type?.map((type) => ({ id: type.id })) || []),
    ];

    const response = await postJson(
        `${OutpatientClinic.Tasks}?limit=${pagination.pageSize}&page=${pagination.pageIndex + 1}`,
        {
            endDate: filter.endDate ? DateTime.from(filter.endDate).format('YYYY-MM-DD') : undefined,
            episodeCategory: filter.episodeCategory?.length ? filter.episodeCategory : undefined,
            performerPractitioner: filter.performerPractitioner ?? undefined,
            performerPractitionerProfession: filter.performerPractitionerProfession?.length
                ? filter.performerPractitionerProfession
                : undefined,
            searchText: filter.searchText ?? undefined,
            sortBy: sorting?.[0]?.id ?? undefined,
            sortDirection: sorting?.[0]?.id
                ? sorting[0]?.desc
                    ? SortDirection.descending
                    : SortDirection.ascending
                : undefined,
            startDate: filter.startDate ? DateTime.from(filter.startDate).format('YYYY-MM-DD') : undefined,
            type: typeFilter.length > 0 ? typeFilter : undefined,
        },
        abortSignal
    );

    if (!response?.json?.items) {
        throw new Error('Failed to fetch tasks');
    }

    return {
        items: response.json.items.map(outpatientClinicTaskMapper),
        total: response.json.total,
    };
}
